import { fileURLToPath } from 'node:url'

import { debugDir, debugFn } from '@socketsecurity/registry/lib/debug'
import { logger } from '@socketsecurity/registry/lib/logger'
import { readPackageJson } from '@socketsecurity/registry/lib/packages'

import constants, { FLAG_DRY_RUN, PACKAGE_JSON } from '../constants.mts'
import { getAlertsMapFromPurls } from '../utils/alerts-map.mts'
import { isAddCommand } from '../utils/cmd.mts'
import { debugScan } from '../utils/debug.mts'
import { safeNpmSpecToPurl } from '../utils/npm-spec.mts'
import { logAlertsMap } from '../utils/socket-package-alert.mts'

import type { AlertsByPurl } from '../utils/socket-package-alert.mts'
import type { Spinner } from '@socketsecurity/registry/lib/spinner'

function extractPackagePurlsFromCommandArgs(
  rawArgs: string[] | readonly string[],
): string[] {
  const packagePurls: string[] = []

  const packageArgs = rawArgs
    .slice(1)
    .filter(a => !a.startsWith('-') && a !== '--')

  for (const pkgSpec of packageArgs) {
    const purl = safeNpmSpecToPurl(pkgSpec)
    if (purl) {
      packagePurls.push(purl)
    }
  }

  return packagePurls
}

export type DlxFlags = {
  // Options whose value names a package to download.
  package: ReadonlySet<string>
  // Other options that take the next argument as their value.
  withValue: ReadonlySet<string>
}

/**
 * Package option values, or else the first positional, name packages. Later
 * positionals are arguments for the launched binary.
 */
function extractPackagePurlsFromDlxArgs(
  rawArgs: string[] | readonly string[],
  dlxFlags: DlxFlags,
): string[] {
  const pkgSpecs: string[] = []
  const args = rawArgs.slice(1)
  for (let i = 0, { length } = args; i < length; i += 1) {
    const arg = args[i]!
    if (arg === '--') {
      break
    }
    if (arg.startsWith('-')) {
      const eqIndex = arg.indexOf('=')
      const name = eqIndex === -1 ? arg : arg.slice(0, eqIndex)
      const isPackageFlag = dlxFlags.package.has(name)
      if (eqIndex !== -1) {
        if (isPackageFlag) {
          pkgSpecs.push(arg.slice(eqIndex + 1))
        }
        continue
      }
      const value = args[i + 1]
      if (
        (isPackageFlag || dlxFlags.withValue.has(name)) &&
        value !== undefined &&
        !value.startsWith('-')
      ) {
        if (isPackageFlag) {
          pkgSpecs.push(value)
        }
        i += 1
      }
      continue
    }
    if (!pkgSpecs.length) {
      pkgSpecs.push(arg)
    }
    break
  }

  const packagePurls: string[] = []
  for (const pkgSpec of pkgSpecs) {
    const purl = safeNpmSpecToPurl(pkgSpec)
    if (purl) {
      packagePurls.push(purl)
    }
  }
  return packagePurls
}

/**
 * Extract package PURLs from package.json for install/update commands.
 * Used by: pnpm, yarn.
 */
async function extractPackagePurlsFromPackageJson(
  cwd = process.cwd(),
): Promise<string[]> {
  const packagePurls: string[] = []

  try {
    const pkgJson = await readPackageJson(cwd)

    const allDeps = {
      ...pkgJson.dependencies,
      ...pkgJson.devDependencies,
      ...pkgJson.optionalDependencies,
      ...pkgJson.peerDependencies,
    }

    for (const { 0: name, 1: version } of Object.entries(allDeps)) {
      const purl = safeNpmSpecToPurl(
        typeof version === 'string' ? `${name}@${version}` : name,
      )
      if (purl) {
        packagePurls.push(purl)
      }
    }

    debugScan('start', packagePurls.length)
  } catch (e) {
    debugFn(
      'warn',
      `${PACKAGE_JSON} not found or invalid during dependency scanning`,
    )
    debugDir('error', e)
  }

  return packagePurls
}

export type PackageScanOptions = {
  acceptRisks: boolean
  apiToken?: string | undefined
  command: string | undefined
  cwd?: string | URL
  dlxCommands?: Set<string>
  dlxFlags?: DlxFlags | undefined
  installCommands: Set<string>
  managerName: string
  nothrow?: boolean
  rawArgs: string[] | readonly string[]
  spinner?: Spinner | undefined
  viewAllRisks: boolean
}

export type PackageScanResult = {
  alertsMap?: AlertsByPurl
  shouldExit: boolean
}

/**
 * Scan packages and log alerts if found.
 */
export async function scanPackagesAndLogAlerts(
  options: PackageScanOptions,
): Promise<PackageScanResult> {
  const {
    acceptRisks,
    apiToken,
    command,
    dlxCommands,
    dlxFlags = { package: new Set(), withValue: new Set() },
    installCommands,
    managerName,
    nothrow = true,
    rawArgs,
    spinner,
    viewAllRisks,
  } = options

  let { cwd = process.cwd() } = options
  if (cwd instanceof URL) {
    cwd = fileURLToPath(cwd)
  }

  // Check if this is a command that needs security scanning.
  const isDlxCommand = dlxCommands && command && dlxCommands.has(command)
  const isInstallCommand = command && installCommands.has(command)
  const needsScanning = isDlxCommand || isInstallCommand

  if (!needsScanning || rawArgs.includes(FLAG_DRY_RUN)) {
    return { shouldExit: false }
  }

  // Extract package names from command arguments before any downloads.
  let packagePurls: string[] = []

  if (isDlxCommand) {
    packagePurls = extractPackagePurlsFromDlxArgs(rawArgs, dlxFlags)
  } else if (isAddCommand(command)) {
    packagePurls = extractPackagePurlsFromCommandArgs(rawArgs)
  } else if (isInstallCommand) {
    // For install/update, scan dependencies from package.json.
    // Note: This scans direct dependencies only.
    packagePurls = await extractPackagePurlsFromPackageJson(cwd)
  }

  if (!packagePurls.length) {
    return { shouldExit: false }
  }

  debugScan('start', packagePurls.length)
  debugDir('inspect', { packagePurls })

  try {
    const alertsMap = await getAlertsMapFromPurls(packagePurls, {
      apiToken,
      filter: acceptRisks
        ? { actions: ['error'], blocked: true }
        : { actions: ['error', 'monitor', 'warn'] },
      nothrow,
      spinner,
    })

    if (alertsMap.size) {
      process.exitCode = 1
      spinner?.stop()
      logAlertsMap(alertsMap, {
        hideAt: viewAllRisks ? 'none' : 'middle',
        output: process.stderr,
      })

      const errorMessage = `Socket ${managerName} exiting due to risks.${
        viewAllRisks
          ? ''
          : `\nView all risks - Rerun with environment variable ${constants.SOCKET_CLI_VIEW_ALL_RISKS}=1.`
      }${
        acceptRisks
          ? ''
          : `\nAccept risks - Rerun with environment variable ${constants.SOCKET_CLI_ACCEPT_RISKS}=1.`
      }`.trim()

      logger.error(errorMessage)
      return { alertsMap, shouldExit: true }
    }
  } catch (e) {
    spinner?.stop()
    // Re-throw process.exit errors from tests.
    if (e instanceof Error && e.message === 'process.exit called') {
      throw e
    }
    debugScan('error', undefined, e)
    // Continue with installation if scanning fails.
  }

  debugScan('complete', packagePurls.length)
  debugDir('inspect', { args: rawArgs.slice(1) })

  return { shouldExit: false }
}
